import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Shield, Award, Lock, CheckCircle, Clock, BadgeCheck, AlertCircle,
  Building2, Mail, FileText, ExternalLink, ChevronDown, ChevronUp,
  FileSearch, Calendar, Loader2, Search, Eye, Download, Settings,
  BarChart3, Globe, Key, ShieldCheck, Users, Database, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CustomDomainTrustCenterPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    compliance: true,
    documents: false,
    riskProfile: true,
    productSecurity: true
  });
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchTrustCenterByDomain();
  }, []);

  const fetchTrustCenterByDomain = async () => {
    try {
      const hostname = window.location.hostname;
      const response = await axios.get(`${API}/api/trust-center/public/by-domain?domain=${encodeURIComponent(hostname)}`);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch trust center by domain:', err);
      if (err.response?.status === 404) {
        setError({
          title: 'Trust Center Not Found',
          message: 'No Trust Center is configured for this domain.'
        });
      } else if (err.response?.status === 403) {
        setError({
          title: 'Access Denied',
          message: 'This Trust Center is not publicly accessible.'
        });
      } else {
        setError({
          title: 'Failed to Load',
          message: 'Unable to load the Trust Center. Please try again later.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatFrameworkName = (framework) => {
    const names = {
      'ISO27001': 'ISO/IEC 27001',
      'SOC2': 'SOC 2 Type II',
      'Essential8': 'Essential 8',
      'PCIDSS': 'PCI DSS',
      'HIPAA': 'HIPAA',
      'ISO42001': 'ISO/IEC 42001'
    };
    return names[framework] || framework;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading Trust Center...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 shadow-lg">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">{error.title}</h2>
            <p className="text-slate-600">{error.message}</p>
            <p className="text-sm text-slate-500 mt-4">
              Domain: {window.location.hostname}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            {data.organization_logo ? (
              <img 
                src={data.organization_logo} 
                alt={data.organization_name} 
                className="h-10 w-auto object-contain"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-sm">
                  {data.organization_name?.charAt(0) || 'T'}
                </span>
              </div>
            )}
            <div>
              <h1 className="text-xl font-semibold text-slate-900">{data.organization_name || 'Trust Center'}</h1>
              <p className="text-xs text-slate-500">Trust Center</p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-white border-b-4 border-teal-600">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Start your security review</h2>
              <p className="text-slate-600">
                Access our security documentation and compliance reports
              </p>
            </div>
            {data.contact_email && (
              <a 
                href={`mailto:${data.contact_email}?subject=Security Information Request`}
                className="inline-flex items-center gap-2 text-teal-700 hover:text-teal-800 font-medium text-sm"
              >
                <Mail className="w-4 h-4" />
                Ask for information
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search content"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-20 py-3 bg-white border-slate-200 rounded-lg shadow-sm text-base"
            data-testid="search-input"
          />
          <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">
            ctrl K
          </span>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Overview Card */}
          <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Overview</CardTitle>
                </div>
                <button 
                  onClick={() => toggleSection('overview')}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  {expandedSections.overview ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              </div>
            </CardHeader>
            {expandedSections.overview && (
              <CardContent className="pt-2">
                <p className="text-slate-600 text-sm leading-relaxed">
                  {data.custom_message || `Welcome to ${data.organization_name}'s Trust Center. We are committed to maintaining the highest standards of security, privacy, and compliance.`}
                </p>
              </CardContent>
            )}
          </Card>

          {/* Compliance Card */}
          <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <BadgeCheck className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Compliance</CardTitle>
                </div>
                <button 
                  onClick={() => toggleSection('compliance')}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  {expandedSections.compliance ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              </div>
            </CardHeader>
            {expandedSections.compliance && (
              <CardContent className="pt-2">
                {data.certifications && data.certifications.length > 0 ? (
                  <div className="space-y-3">
                    {data.certifications.map((cert, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-sm">ISO</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{cert.framework}</span>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          </div>
                          {cert.description && (
                            <p className="text-xs text-slate-500 mt-0.5">{cert.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {data.compliance_coverage && data.compliance_coverage.filter(c => c.compliance_percentage > 0).slice(0, 3).map((coverage, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className="w-12 h-12 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold text-xs">
                            {coverage.framework.substring(0, 3).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{formatFrameworkName(coverage.framework)}</span>
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          </div>
                          <p className="text-xs text-slate-500">{coverage.compliance_percentage}% compliant</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>

          {/* Trusted By Section - Full Width (Optional) */}
          {data.visible_sections?.trusted_by !== false && (
            <div className="lg:col-span-2 bg-slate-50 rounded-lg py-8 px-6 text-center">
              <p className="text-sm text-slate-600 mb-4">
                <span className="font-semibold text-slate-900">{data.organization_name}</span> is reviewed and trusted by
              </p>
              <div className="flex items-center justify-center gap-8">
                {data.organization_logo ? (
                  <img 
                    src={data.organization_logo} 
                    alt={data.organization_name}
                    className="h-16 w-auto object-contain opacity-70"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
                    <span className="text-white font-bold text-xl">
                      {data.organization_name?.charAt(0) || 'T'}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-3">{data.organization_name}</p>
            </div>
          )}

          {/* Documents Card */}
          {((data.documents && data.documents.length > 0) || (data.pentest_reports && data.pentest_reports.length > 0)) && (
            <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Documents</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {data.pentest_reports && data.pentest_reports.map((report, index) => (
                    <div 
                      key={`pentest-${index}`}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100"
                    >
                      <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <FileSearch className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Reports</p>
                        <p className="text-sm font-medium text-slate-900 truncate">{report.title || 'Pentest Report'}</p>
                      </div>
                      <Lock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    </div>
                  ))}
                  {data.documents && data.documents.map((doc, index) => (
                    <div 
                      key={`doc-${index}`}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100"
                    >
                      <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Document</p>
                        <p className="text-sm font-medium text-slate-900 truncate">{doc.name}</p>
                      </div>
                      {doc.url ? (
                        <a href={doc.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 text-slate-400 hover:text-teal-600" />
                        </a>
                      ) : (
                        <Lock className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Policies Card */}
          {data.visible_sections?.policies !== false && data.policies && data.policies.length > 0 && (
            <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Policies</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-2">
                  {data.policies.map((policy, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg"
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-slate-900">{policy.name}</p>
                        {policy.description && (
                          <p className="text-xs text-slate-500 mt-0.5">{policy.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Risk Profile Card */}
          <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Risk Profile</CardTitle>
                </div>
                <button 
                  onClick={() => toggleSection('riskProfile')}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  {expandedSections.riskProfile ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              </div>
            </CardHeader>
            {expandedSections.riskProfile && (
              <CardContent className="pt-2">
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">Data Access Level</span>
                    </div>
                    <span className="text-sm font-medium text-slate-900 bg-green-100 text-green-700 px-2 py-0.5 rounded">Public</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">Impact Level</span>
                    </div>
                    <span className="text-sm font-medium text-slate-900 bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Moderate</span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">Recovery Time Objective</span>
                    </div>
                    <span className="text-sm font-medium text-slate-900">10 hours</span>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>

          {/* Product Security Card */}
          <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <Settings className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Product Security</CardTitle>
                </div>
                <button 
                  onClick={() => toggleSection('productSecurity')}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  {expandedSections.productSecurity ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              </div>
            </CardHeader>
            {expandedSections.productSecurity && (
              <CardContent className="pt-2">
                <p className="text-sm text-slate-600 leading-relaxed">
                  {data.organization_name} gives special attention to enterprise features like access control and single sign-on (SSO). Our platform is built with security-first principles, including end-to-end encryption, role-based access controls, and comprehensive audit logging.
                </p>
              </CardContent>
            )}
          </Card>

          {/* Compliance Coverage Card */}
          {data.compliance_coverage && data.compliance_coverage.length > 0 && (
            <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Compliance Coverage</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {data.compliance_coverage.map((framework, index) => (
                    <div key={index} className="p-4 bg-slate-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-slate-900">{formatFrameworkName(framework.framework)}</span>
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                      <div className="space-y-2">
                        <Progress value={framework.compliance_percentage} className="h-2" />
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">
                            {framework.compliant_controls} / {framework.total_controls} controls
                          </span>
                          <span className="font-medium text-teal-700">{framework.compliance_percentage}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Security Practices Card */}
          {data.security_practices && data.security_practices.length > 0 && (
            <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <Lock className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Security Practices</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {data.security_practices.map((practice, index) => (
                    <div key={index}>
                      <h4 className="font-medium text-slate-900 mb-3">{practice.category}</h4>
                      <ul className="space-y-2">
                        {practice.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start gap-2 text-sm text-slate-600">
                            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8 mt-auto">
        <div className="max-w-6xl mx-auto px-4">
          <a
            href="https://cyberforte.com.au"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-2 group"
            data-testid="footer-cyberforte-link"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_compliance-hub-261/artifacts/r86y6lnz_CyberForteIcon.png"
              alt="Cyber Forte"
              className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
            />
            <span className="text-sm text-slate-500 group-hover:text-teal-700 transition-colors">
              Built on Comply by Cyber Forte
            </span>
          </a>
        </div>
      </footer>
    </div>
  );
}
